import * as ReactDOM from "react-dom/client";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { I18nextProvider } from "react-i18next";
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import i18n from './utils/i18n';
import { idbStorage } from './services/core/idb-storage.service';
import App from "./app/app";
import "./styles/main.scss";
import DirectionProvider from "./app/components/core/direction-provider";
import './utils/speakpipe-override'; // דריסה חזקה של SpeakPipe

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Persist cache - כך שהקאש יישמר גם אחרי רענון דף
// זה מאפשר למשתמשים לטעון את הספרים פעם אחת בלבד
// הקאש נשמר לתמיד (או עד שהגרסה משתנה - useBookContent בודק את הגרסה בכל קריאה)
// Books are saved in IndexedDB, not localStorage: localStorage holds about 5 MB,
// three or four books filled it, and later saves failed silently.
const persister = createAsyncStoragePersister({
  storage: idbStorage,
  key: 'REACT_QUERY_BOOK_CACHE',
});

// The old localStorage copy may hold books with blank verses (saved after a server
// restart sent unscrambled text). Drop it so each device downloads clean books once.
try {
  window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
} catch {
  // storage blocked; nothing to clean
}

persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24 * 365, // שנה שלמה - הקאש נשמר לתמיד
  // שמור את כל ה-queries של הספרים
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      // שמור רק את ה-queries של הספרים
      return query.queryKey[0] === 'books' && query.queryKey[1] === 'content';
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <QueryClientProvider client={queryClient}>
    <StyledEngineProvider injectFirst>
      <I18nextProvider i18n={i18n}>
        <GoogleOAuthProvider
          clientId="30490746817-pmfiogl6451tmi0hb312fgpiqlcumeeh.apps.googleusercontent.com"
          onScriptLoadError={() => console.error("Google OAuth script load error")}
          onScriptLoadSuccess={() => console.log("Google OAuth script loaded successfully")}
        >
          <DirectionProvider>
            <App />
          </DirectionProvider>
        </GoogleOAuthProvider>
      </I18nextProvider>
    </StyledEngineProvider>
    {import.meta.env.DEV && (
      <ReactQueryDevtools initialIsOpen={false} />
    )}
  </QueryClientProvider>
);